import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Clock,
  BookOpen,
  Users,
  Trash2,
  ArrowLeft,
  Pen,
  CheckIcon,
  X as XIcon,
  Copy,
  CalendarClock,
  GraduationCap,
} from "lucide-react";
import {
  getGetAssignmentsFromTeacherQueryKey,
  useDeleteAssignmentsFromTeacher,
  useEditAssignment,
  useGetAssignmentFromTeacher,
} from "@packages/api/madlenAPI";
import SingleAssignmentOverviewTab from "../componnets/SingleAssignmentOverviewTab";
import SingleAssignmentAnalyticTab from "../componnets/SingleAssignmentAnalyticTab";
import SingleAssignmentEditTab from "../componnets/SingleAssignmentEditTab";
import SingleAssignmentTab from "../componnets/SingleAssignmentTab";
import PDFDownloadCard from "../componnets/PDFDownloadCard";
import { formatDate } from "@packages/utils";
import { Button, Text } from "../../../../core";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { CourseData } from "@packages/constant/CourseData";
import AssignmentSuccessModal from "../componnets/AssignmentSuccessModal";
import { BaseAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import { DateTimePicker } from "@core/datepicker";

interface EditableFieldProps {
  isEditing: boolean;
  value: string;
  defaultValue: string;
  onChange: (value: string) => void;
  onSave: (value: string) => void;
  onCancel: () => void;
  setIsEditing: (value: boolean) => void;
  isHeading?: boolean;
  multiline?: boolean;
  placeholder?: string;
}

function EditableField({
  isEditing,
  value,
  defaultValue,
  onChange,
  onSave,
  onCancel,
  setIsEditing,
  isHeading = false,
  multiline = false,
}: EditableFieldProps) {
  if (isEditing) {
    return (
      <div
        className={`flex items-center ${isHeading ? "mb-2" : "mt-1"} relative`}
      >
        {multiline ? (
          <textarea
            value={value}
            rows={2}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-1 px-2 w-full text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            autoFocus
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && multiline) {
                e.preventDefault();
                if (value.trim() !== defaultValue.trim()) {
                  onSave(value);
                }
                setIsEditing(false);
              } else if (e.key === "Enter" && !multiline) {
                if (value.trim() !== defaultValue.trim()) {
                  onSave(value);
                }
                setIsEditing(false);
              }
            }}
          />
        ) : (
          <input
            type="text"
            value={value}
            className={`bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-1 px-2 ${isHeading ? "text-2xl font-bold" : ""} w-full focus:outline-none focus:ring-2 focus:ring-blue-500`}
            autoFocus
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (value.trim() !== defaultValue.trim()) {
                  onSave(value);
                }
                setIsEditing(false);
              }
            }}
          />
        )}
        <div className="flex flex-col mx-2 gap-2 items-center">
          <Button
            size="sm"
            rounded="full"
            variant="outline"
            bgColor="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
            textColor="text-green-600 dark:text-green-400"
            className="py-1.5 px-1.5 border border-green-200 dark:border-green-700 hover:border-green-400"
            onClick={() => {
              if (value.trim() !== defaultValue.trim()) {
                onSave(value);
              }
              setIsEditing(false);
            }}
          >
            <CheckIcon className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            rounded="full"
            variant="outline"
            bgColor="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
            textColor="text-red-600 dark:text-red-400"
            className="py-1.5 px-1.5 border border-red-200 dark:border-red-700 hover:border-red-400"
            onClick={() => {
              setIsEditing(false);
              onChange(defaultValue);
              onCancel();
            }}
          >
            <XIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center cursor-pointer group ${isHeading ? "" : "mt-1"} hover:bg-gray-50 dark:hover:bg-gray-700 py-1 px-2 rounded-md transition-colors relative`}
      onClick={() => {
        onChange(defaultValue);
        setIsEditing(true);
      }}
    >
      {isHeading ? (
        <Text variant="h1" className="mr-2">
          {defaultValue}
        </Text>
      ) : (
        <Text variant="p" className="text-gray-600 dark:text-gray-400 mr-2">
          {defaultValue || "-"}
        </Text>
      )}
      <Pen
        size={16}
        className="absolute right-1 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      />
    </div>
  );
}

const SingleAssignmentView = () => {
  const { id, tab } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"overview" | "edit" | "analytics">(
    (tab as "overview" | "edit" | "analytics") || "overview",
  );
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isNameEditable, setIsNameEditable] = useState(false);
  const [isDescriptionEditable, setIsDescriptionEditable] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [isEndDateEditable, setIsEndDateEditable] = useState(false);

  const {
    data: assignmentRaw,
    isLoading,
    error,
    queryKey: assignmentQueryKey,
  } = useGetAssignmentFromTeacher(Number(id), {
    query: {
      refetchInterval: (data) =>
        data?.state.data?.status === "PENDING" ? 1000 : false,
    },
  });

  const updateAssignment = useEditAssignment({
    mutation: {
      onMutate: async (variables) => {
        const previousData = queryClient.getQueryData(assignmentQueryKey);
        queryClient.setQueryData(
          assignmentQueryKey,
          (old: BaseAssignmentResponseDto | undefined) => {
            if (!old) return old;
            return {
              ...old,
              name: variables.data.name ?? old.name,
              description: variables.data.description ?? old.description,
              endDate: variables.data.endDate ?? old.endDate,
            };
          },
        );
        return { previousData };
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: assignmentQueryKey,
        });
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(assignmentQueryKey, context?.previousData);
        console.error("Error updating assignment:", error);
      },
    },
  });

  useEffect(() => {
    if (assignmentRaw) {
      setEditedName(assignmentRaw.name);
      setEditedDescription(assignmentRaw.description || "");
    }
  }, [assignmentRaw]);

  const updateName = async (name: string) => {
    try {
      await updateAssignment.mutateAsync({
        assignmentId: Number(id),
        data: { name },
      });
      addToast({
        title: t("notifications.assignment.updateSuccess"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Failed to update name:", error);
      addToast({
        title: t("notifications.assignment.updateError"),
        description: t("notifications.assignment.updateErrorDescription"),
        variant: "error",
        duration: 3000,
      });
    }
  };

  const updateDescription = async (description: string) => {
    try {
      await updateAssignment.mutateAsync({
        assignmentId: Number(id),
        data: { description },
      });
      addToast({
        title: t("notifications.assignment.updateSuccess"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Failed to update description:", error);
      addToast({
        title: t("notifications.assignment.updateError"),
        description: t("notifications.assignment.updateErrorDescription"),
        variant: "error",
        duration: 3000,
      });
    }
  };

  const updateEndDate = async (endDate: string) => {
    try {
      // Make sure we have a valid ISO date string
      if (!endDate) {
        addToast({
          title: t("notifications.assignment.invalidDate"),
          variant: "error",
          duration: 3000,
        });
        return;
      }

      await updateAssignment.mutateAsync({
        assignmentId: Number(id),
        data: { endDate },
      });
      addToast({
        title: t("notifications.assignment.updateSuccess"),
        variant: "success",
        duration: 3000,
      });
      setIsEndDateEditable(false);
    } catch (error) {
      console.error("Failed to update end date:", error);
      addToast({
        title: t("notifications.assignment.updateError"),
        description: t("notifications.assignment.updateErrorDescription"),
        variant: "error",
        duration: 3000,
      });
    }
  };

  const order = useMemo(() => ["OE", "TEST", "TF", "FB"], []);

  const assignment = useMemo(
    () =>
      assignmentRaw
        ? {
            ...assignmentRaw,
            questions: [...assignmentRaw.questions].sort((a, b) => {
              const typeOrder = order.indexOf(a.type) - order.indexOf(b.type);
              return typeOrder === 0 ? a.id - b.id : typeOrder;
            }),
          }
        : undefined,
    [assignmentRaw, order],
  );

  const deleteAssignment = useDeleteAssignmentsFromTeacher({
    mutation: {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAssignmentsFromTeacherQueryKey(),
        });
      },
    },
  });

  const handleDelete = async (assignmentId: number) => {
    try {
      await deleteAssignment.mutateAsync({ assignmentId: assignmentId });
      addToast({
        title: t("assignments.form.deleted"),
        variant: "success",
        duration: 3000,
      });
      navigate("/assignments");
    } catch (error) {
      console.error("Failed to delete lesson plan:", error);
      addToast({
        title: t("assignments.form.deleteError"),
        description: t("assignments.form.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-6 bg-gray-200 dark:bg-gray-700 rounded"
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <Text
              values={{ message: error?.message }}
              variant="p"
              className="text-red-500 dark:text-red-400"
            >
              {t("assignments.error")}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          onClick={() => navigate("/assignments")}
          size="sm"
          rounded="full"
          bgColor="bg-white dark:bg-dark-600 hover:bg-gray-100 dark:hover:bg-dark-700"
          textColor="text-gray-700 dark:text-gray-300"
          className="mb-4 flex items-center shadow-sm p-2 px-4"
        >
          <ArrowLeft size={18} className="mr-1" />
          <span className="text-sm">{t("common.back")}</span>
        </Button>

        {/* Assignment Image */}
        {assignment.assignmentImageUrl && (
          <div className="bg-white dark:bg-dark-600 rounded-t-xl shadow-sm overflow-hidden">
            <div className="relative">
              <img
                src={assignment.assignmentImageUrl}
                alt={t("assignments.image.alt")}
                className="w-full h-auto object-cover max-h-[200px]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none"></div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white dark:bg-dark-600 shadow-sm p-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4">
            <div className="flex-1">
              <EditableField
                isEditing={isNameEditable}
                value={editedName}
                defaultValue={assignment.name}
                onChange={setEditedName}
                onSave={updateName}
                onCancel={() => setEditedName(assignment.name)}
                setIsEditing={setIsNameEditable}
                isHeading={true}
              />

              <EditableField
                isEditing={isDescriptionEditable}
                value={editedDescription}
                defaultValue={assignment.description || ""}
                onChange={setEditedDescription}
                onSave={updateDescription}
                onCancel={() =>
                  setEditedDescription(assignment.description || "")
                }
                setIsEditing={setIsDescriptionEditable}
                multiline={true}
              />
            </div>
            <div className="items-center flex gap-4 my-2 md:mt-0">
              <Text
                variant="span"
                className={`font-medium text-sm px-2 py-1 rounded-full ${
                  assignment.difficulty === "EASY"
                    ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-100"
                    : assignment.difficulty === "MEDIUM"
                      ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-100"
                      : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-100"
                }`}
              >
                {t(
                  `assignments.generator.questions.difficulties.${assignment.difficulty?.toLowerCase()}`,
                )}
              </Text>
              <Text
                variant="span"
                className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap
              ${
                assignment.status === "ASSIGNED"
                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100"
                  : assignment.status === "DRAFT"
                    ? "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    : "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100"
              }`}
              >
                {t("assignments.filters." + assignment.status.toLowerCase())}
              </Text>

              <Button
                size="sm"
                className={`flex items-center justify-center rounded-full p-1 transition-all duration-300 bg-red-500 text-white hover:shadow-lg overflow-hidden hover:px-4 group`}
                onClick={() => setShowDeletePopup(assignment.id)}
              >
                <Trash2 className="w-4 h-4 ml-1" />
                <span className="group-hover:mr-2 max-w-0 group-hover:max-w-[100px] whitespace-nowrap overflow-hidden transition-all duration-300 opacity-0 group-hover:opacity-100 ml-0 group-hover:ml-2">
                  {t("common.delete")}
                </span>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              {assignment.type === "HOMEWORK" ? (
                assignment.endDate ? (
                  <div className="flex items-center relative">
                    <DateTimePicker
                      value={assignment.endDate || new Date().toISOString()}
                      onChange={() => {}}
                      minDate={new Date()}
                      className={isEndDateEditable ? "w-full" : ""}
                      placeholder={t(
                        "assignments.generator.properties.selectEndDate",
                      )}
                      onFinish={(date: Date) => {
                        updateEndDate(date.toISOString());
                      }}
                      isOpen={isEndDateEditable}
                      onOpenChange={setIsEndDateEditable}
                      baseComponent={
                        <div className="flex items-center cursor-pointer">
                          <Calendar size={16} className="mr-2" />
                          {t("assignments.card.due")}:{" "}
                          <span>
                            {formatDate(assignment.endDate, i18n.language)}
                          </span>
                          <Button
                            size="xs"
                            rounded="full"
                            variant="ghost"
                            className="ml-1"
                          >
                            <Pen
                              size={14}
                              className="text-gray-500 dark:text-gray-400"
                            />
                          </Button>
                        </div>
                      }
                    />
                  </div>
                ) : (
                  "-"
                )
              ) : assignment.endDate ? (
                formatDate(assignment.endDate, i18n.language)
              ) : (
                "-"
              )}
            </div>
            {assignment.type === "QUIZ" && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Clock size={16} className="mr-2" />
                {(() => {
                  if (!assignment.duration) return "-";
                  const diffMs = assignment.duration * 60 * 1000;

                  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const hours = Math.floor(
                    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
                  );
                  const minutes = Math.floor(
                    (diffMs % (1000 * 60 * 60)) / (1000 * 60),
                  );

                  const parts = [];
                  if (days > 0) {
                    parts.push(`${days} ${t("common.time.days")}`);
                  }
                  if (hours > 0) {
                    parts.push(`${hours} ${t("common.time.hours")}`);
                  }
                  if (minutes > 0) {
                    parts.push(`${minutes} ${t("common.time.minutes")}`);
                  }

                  return parts.length > 0
                    ? parts.join(" ")
                    : "0 " + t("common.time.minutes");
                })()}
              </div>
            )}
            {assignment.grade && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <GraduationCap size={16} className="mr-2" />
                {t("assignments.grade", { grade: assignment.grade })}
              </div>
            )}
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <BookOpen size={16} className="mr-2" />
              {t(
                "courseType." +
                  CourseData[assignment.courseType]?.name(
                    Number(assignment.grade),
                  ),
              )}
            </div>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Users size={16} className="mr-2" />
              {assignment.assignedStudentIds.length}{" "}
              {t("assignments.card.studentsAssigned")}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <SingleAssignmentTab
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          assignmentId={id ?? ""}
        />

        <PDFDownloadCard
          setShowSuccessModal={setShowSuccessModal}
          assignment={assignment}
          showAssignStudent={activeTab === "edit"}
        />

        {/* Tab Content */}
        <div className="rounded-xl pt-0">
          <div className="relative min-h-[500px]">
            <div className={`${activeTab === "overview" ? "block" : "hidden"}`}>
              <SingleAssignmentOverviewTab
                assignment={assignment}
                isLoading={isLoading}
                error={error}
                setShowSuccessModal={setShowSuccessModal}
              />
            </div>

            <div className={`${activeTab === "edit" ? "block" : "hidden"}`}>
              <SingleAssignmentEditTab assignment={assignment} />
            </div>

            <div
              className={`${activeTab === "analytics" ? "block" : "hidden"}`}
            >
              <SingleAssignmentAnalyticTab
                assignmentId={assignment.id}
                setActiveTab={(tab: string) =>
                  setActiveTab(tab as "overview" | "edit" | "analytics")
                }
                questions={assignment.questions}
              />
            </div>
          </div>
        </div>
        {activeTab === "edit" && (
          <PDFDownloadCard
            assignment={assignment}
            showAssignStudent={true}
            setShowSuccessModal={setShowSuccessModal}
          />
        )}
      </div>

      {showDeletePopup && (
        <DeleteConfirmPopUp
          title={t("assignments.form.deleteConfirmation", {
            title: assignment.name,
          })}
          showDeletePopup={showDeletePopup}
          setShowDeletePopup={setShowDeletePopup}
          handleDelete={handleDelete}
        />
      )}

      {showSuccessModal && (
        <AssignmentSuccessModal
          onClose={() => {
            setShowSuccessModal(false);
            setActiveTab("edit");
          }}
          onGoBack={() => navigate("/")}
        />
      )}
    </div>
  );
};

export default SingleAssignmentView;
